import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  /** @type {number | null} */
  #resizeFrame = null;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedResize = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.#resizeFrame) return;
        this.#resizeFrame = requestAnimationFrame(() => {
          this.#resizeFrame = null;
          this.#calculateTruncation();
        });
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      this.#debouncedResize = debounce(() => this.#calculateTruncation(), 150);
      /** @type {Window} */
      (window).addEventListener('resize', this.#debouncedResize, { passive: true });
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title.
   * Reads and writes are separated to avoid layout thrashing when
   * multiple ProductTitle instances fire their ResizeObserver in the same frame.
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    // --- READ PHASE (may force layout) ---
    const containerHeight = this.clientHeight;
    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    // --- COMPUTE (no DOM access) ---
    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    // --- WRITE PHASE (batched) ---
    // Only update if the value actually changed to avoid unnecessary style recalc
    if (textElement.style.webkitLineClamp !== String(maxLines)) {
      textElement.style.display = '-webkit-box';
      textElement.style.webkitBoxOrient = 'vertical';
      textElement.style.overflow = 'hidden';
      textElement.style.textOverflow = 'ellipsis';
      textElement.style.webkitLineClamp = String(maxLines);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.#debouncedResize) {
      this.#debouncedResize.cancel();
      window.removeEventListener('resize', this.#debouncedResize);
    }
    if (this.#resizeFrame) {
      cancelAnimationFrame(this.#resizeFrame);
    }
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
